import React, { lazy } from "react";
import { createBrowserRouter, redirect } from "react-router-dom";
import { MainLayout } from "../Layout";
import { store } from "../store/store";

import NewHome from "../pages/NewHome";

// Route-level code-splitting: each route is loaded on demand
const ErrorPage = lazy(() => import("../pages/ErrorPage"));
const NotFound = lazy(() => import("../pages/NotFound"));

const SuspendedErrorPage = () => (
  <React.Suspense fallback={null}>
    <ErrorPage />
  </React.Suspense>
);

const SuspendedNotFound = () => (
  <React.Suspense fallback={null}>
    <NotFound />
  </React.Suspense>
);

// Route-level code-splitting: each non-critical route is loaded on demand
const AboutUs = lazy(() => import("../pages/AboutUs"));
const ContactUs = lazy(() => import("../pages/ContactUs"));
const FAQ = lazy(() => import("../pages/FAQ"));
const Policy = lazy(() => import("../pages/Policy"));
const DownloadApp = lazy(() => import("../pages/DownloadAp"));
const ProviderShowProfile = lazy(() => import("../pages/ProviderShowProfile"));
const ServicesQuestion = lazy(() => import("../pages/Services/ServicesQuestion"));
const ServiceFirstLevelCategory = lazy(() =>
  import("../pages/Services/ServiceFirstLevelCategory"),
);
const ServiceSecondLevelCategory = lazy(() =>
  import("../pages/Services/ServiceSecondLevelCategory"),
);
const ServiceThirdLevelCategory = lazy(() =>
  import("../pages/Services/ServiceThirdLevelCategory"),
);
const ServicesCreate = lazy(() => import("../pages/Services/ServicesCreate"));
const ServicesPrice = lazy(() => import("../pages/Services/ServicesPrice"));
const UserAddresses = lazy(() => import("../pages/Services/UserAddresses"));
const FindingProject = lazy(() => import("../pages/FindingProject"));
const OrderOffers = lazy(() => import("../pages/OrderOffers"));
const ProjectDetails = lazy(() => import("../pages/ProjectDetails"));
const CompleteProviderProfile = lazy(() =>
  import("../pages/CompleteProviderProfile"),
);
const CompleteProviderAddress = lazy(() =>
  import("../pages/CompleteProviderAddress"),
);
const CompleteProviderServices = lazy(() =>
  import("../pages/CompleteProviderServices"),
);
const CompleteProviderCertificates = lazy(() =>
  import("../pages/CompleteProviderCertificates"),
);
const CompleteProviderAreas = lazy(() =>
  import("../pages/CompleteProviderAreas"),
);
const ProfileLayout = lazy(() => import("../pages/ProfileLayout"));
const Categories = lazy(() => import("../pages/Categories"));
const Blog = lazy(() => import("../pages/Blog"));
const BlogDetail = lazy(() => import("../pages/BlogDetail"));
const CategoryDetail = lazy(() => import("../pages/CategoryDetails"));
const PromotePage = lazy(() => import("../pages/PromotePage"));
const PaymentCallbackPage = lazy(() => import("../pages/PaymentCallbackPage"));
const ServerError = lazy(() => import("../pages/ServerError"));
const AppInstallPage = lazy(() => import("../pages/AppInstallPage"));

// Profile sub-routes code-splitting
const NotificationOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/NotificationOutlet"),
);
const DetailProfileOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/DetailProfileOutlet"),
);
const RatingOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/RatinOutlet"),
);
const OrderRatingPage = lazy(() =>
  import("../pages/ProfileLayout/Outlets/OrderRatingPage"),
);
const ChatOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/ChatOutlet"),
);
const InviteOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/InviteOutlet"),
);
const OrdersOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/OrdersOutlet"),
);
const ProjectsOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/ProjectsOutlet"),
);
const WalletOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/WalletOutlet"),
);
const DiscountsOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/DiscountsOutlet"),
);
const DocsOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/DocsOutlet"),
);
const TicketOutlet = lazy(() =>
  import("../pages/ProfileLayout/Outlets/TicketOutlet"),
);
const NewTicket = lazy(() =>
  import("../pages/ProfileLayout/Outlets/NewTicket"),
);
const TicketDetail = lazy(() =>
  import("../pages/ProfileLayout/Outlets/TicketOutlet/TicketDetail"),
);

const completeProviderLoader = (step) => {
  const state = store.getState();
  const user = state.userSlice.user;
  const role = state.userSlice.role;

  if (role !== "provider") {
    return redirect("/");
  }
  // STEP 1: profile
  const profileCompleted =
    user?.fullname &&
    user?.email &&
    user?.nationalCode &&
    user?.docs?.nationalCardImg;

  // STEP 2: address
  const addressCompleted = Boolean(user?.defaultAddress);

  // 🔒 profile page
  if (step === "profile" && !profileCompleted) {
    return null; // allow
  }

  // 🔒 address page
  if (step === "address") {
    if (!profileCompleted) {
      return redirect("/complete-provider");
    }
    if (addressCompleted) {
      return redirect("/complete-provider-address");
    }
    return null;
  }

  // 🔒 services page
  if (step === "services") {
    if (!profileCompleted) {
      return redirect("/complete-provider");
    }
    if (!addressCompleted) {
      return redirect("/complete-provider-address");
    }
    return null;
  }

  return null;
};

const handleTokenExist = () => {
  const { token } = store.getState().userSlice;
  if (!token) {
    return redirect("/");
  }
  return;
};

const offerLoader = () => {
  const state = store.getState();
  const role = state.userSlice.role;

  if (role === "provider") {
    return redirect("/");
  }
};

const projectsLoader = () => {
  const state = store.getState();
  const user = state.userSlice.user;
  const role = state.userSlice.role;
  if (role === "customer") {
    return redirect("/");
  }
  if (!user?.isComplete) {
    return redirect("/profile/details");
  }
};

const router = createBrowserRouter([
  {
    path: "",
    element: <MainLayout />,
    errorElement: <SuspendedErrorPage />,
    children: [
      {
        index: true,
        element: <NewHome />,
      },
      {
        path: "about",
        element: <AboutUs />,
      },
      {
        path: "contact",
        element: <ContactUs />,
      },
      {
        path: "coming-soon",
        element: <SuspendedErrorPage />,
      },
      {
        path: "order",
        element: <SuspendedErrorPage />,
      },
      {
        path: "faq",
        element: <FAQ />,
      },
      {
        path: "provider-show-profile/:id",
        element: <ProviderShowProfile />,
      },
      {
        path: "policy",
        element: <Policy />,
      },
      {
        path: "download-app",
        element: <DownloadApp />,
      },
      {
        path: "services/question/:categoryId",
        element: <ServicesQuestion />,
      },
      {
        path: "services/category",
        element: <ServiceFirstLevelCategory />,
      },
      {
        path: "services/category/second",
        element: <ServiceSecondLevelCategory />,
      },
      {
        path: "services/category/third",
        element: <ServiceThirdLevelCategory />,
      },
      {
        path: "services/create",
        element: <ServicesCreate />,
      },
      {
        path: "services/price",
        element: <ServicesPrice />,
      },
      {
        path: "services/address",
        element: <UserAddresses />,
      },
      {
        path: "finding-project",
        element: <FindingProject />,
      },
      {
        path: "order-offers/:id",
        element: <OrderOffers />,
      },
      {
        path: "project-details/:shapeType/:id",
        element: <ProjectDetails />,
      },
      {
        path: "/complete-provider",
        element: <CompleteProviderProfile />,
      },
      {
        path: "/complete-provider-address",
        element: <CompleteProviderAddress />,
      },
      {
        path: "/complete-provider-services",
        element: <CompleteProviderServices />,
      },
      {
        path: "/complete-provider-docs",
        element: <CompleteProviderCertificates />,
      },
      {
        path: "/complete-provider-areas",
        element: <CompleteProviderAreas />,
      },
      {
        path: "/profile",
        element: <ProfileLayout />,
        children: [
          { index: true, element: <NotificationOutlet /> },
          { path: "details", element: <DetailProfileOutlet /> },
          { path: "ratings", element: <RatingOutlet /> },
          { path: "order-comment/:id", element: <OrderRatingPage /> },
          { path: "chats", element: <ChatOutlet /> },
          { path: "invite", element: <InviteOutlet /> },
          { path: "orders", element: <OrdersOutlet /> },
          { path: "projects", element: <ProjectsOutlet /> },
          { path: "wallet", element: <WalletOutlet /> },
          { path: "discounts", element: <DiscountsOutlet /> },
          { path: "docs", element: <DocsOutlet /> },
          { path: "ticket", element: <TicketOutlet /> },
          { path: "ticket-new", element: <NewTicket /> },
          { path: "ticket/detail/:id", element: <TicketDetail /> },
        ],
      },
      {
        path: "/category",
        element: <Categories />,
      },
      {
        path: "/blog",
        element: <Blog />,
      },
      {
        path: "/blog-detail/:id",
        element: <BlogDetail />,
      },
      {
        path: "/category/:id",
        element: <CategoryDetail />,
      },
      {
        path: "/promote/:id",
        element: <PromotePage />,
      },
      {
        path: "callback/payment/verify",
        element: <PaymentCallbackPage />,
      },
    ],
  },
  {
    path: "/server-error",
    element: <ServerError />,
  },
  {
    path: "/app-install",
    element: <AppInstallPage />,
  },
  {
    path: "*",
    element: <SuspendedNotFound />,
  },
]);

export default router;
